// frontend/src/components/noel/SantaPlanPaymentPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star,
  Crown,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import apiClient from "../../Api/apiConfig";
import "./SantaPlanPaymentPage.css";

const PLANS = {
  "famille-magique": {
    id: "family",
    slug: "famille-magique",
    label: "Famille Magique",
    price: 7.9,
    priceLabel: "7,90 €",
    description: "Le pack complet pour un souvenir unique",
    icon: Star,
  },
  "renne-du-pere-noel": {
    id: "reindeer",
    slug: "renne-du-pere-noel",
    label: "Renne du Père Noël",
    price: 39.9,
    priceLabel: "39,90 €",
    description: "L'expérience ultime avec vidéo personnalisée",
    icon: Crown,
  },
};

// Pour afficher un texte humain selon le code plan
const PLAN_LABELS = {
  none: "Aucun",
  free: "Gratuit",
  family: "Famille Magique",
  reindeer: "Renne du Père Noël",
};

export default function SantaPlanPaymentPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const plan = PLANS[planId];

  const [childName, setChildName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [extraInfo, setExtraInfo] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [clientId, setClientId] = useState(null);
  const [clientError, setClientError] = useState("");

  // 🧠 Gestion plan actuel + montant réel à payer (pour upgrade)
  const [currentPlan, setCurrentPlan] = useState("none"); // "none" | "free" | "family" | "reindeer"
  const [computedAmount, setComputedAmount] = useState(null); // montant réellement facturé (upgrade)

  // Modal recap
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  const isFormValid = Boolean(childName.trim() && parentEmail.trim());

  // ========================================================================
  // 1. Récupérer le clientId PayPal depuis le backend
  // ========================================================================
  useEffect(() => {
    const fetchClientId = async () => {
      try {
        const res = await apiClient.get("/paypal/client-id");
        console.log("[SantaPayment] /paypal/client-id =>", res.data);
        setClientId(res.data?.clientId || null);
        setClientError("");
      } catch (err) {
        console.error("Erreur récupération clientId PayPal:", err);
        setClientError(
          "Impossible de charger la configuration PayPal. Merci de réessayer plus tard."
        );
      }
    };

    fetchClientId();
  }, []);

  // ========================================================================
  // 2. Récupérer le plan déjà actif via l'email parent (si rempli)
  //    -> nécessite une route backend GET /santa/premium/status?email=...
  // ========================================================================
  useEffect(() => {
    const email = parentEmail.trim();
    if (!email) {
      setCurrentPlan("none");
      setComputedAmount(null);
      return;
    }

    // petit check basique de format d'email
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(email)) {
      return;
    }

    let isCancelled = false;

    const fetchCurrentPlan = async () => {
      try {
        console.log("[SantaPayment] Vérification plan actuel pour", email);
        const res = await apiClient.get(
          `/santa/premium/status?email=${encodeURIComponent(email)}`
        );
        if (isCancelled) return;

        const planFromServer = res.data?.plan || "none";
        setCurrentPlan(planFromServer);
      } catch (error) {
        console.error("Erreur récupération plan Père Noël :", error);
        if (!isCancelled) {
          // En cas d'erreur, on ne bloque pas, on repasse à "none"
          setCurrentPlan("none");
        }
      }
    };

    fetchCurrentPlan();

    return () => {
      isCancelled = true;
    };
  }, [parentEmail]);

  // ========================================================================
  // 3. Calcul du montant réel à payer en fonction du plan actuel + cible
  //    - none / free -> prix plein du plan choisi
  //    - family -> upgrade vers reindeer = différence
  //    - même plan -> 0 (et on désactive le paiement)
  //    - reindeer -> 0 si on essaye de descendre (non autorisé)
  // ========================================================================
  useEffect(() => {
    if (!plan) return;

    const target = plan.id; // "family" ou "reindeer"
    let amount = plan.price;

    if (currentPlan === "family" && target === "reindeer") {
      // upgrade Family -> Reindeer
      amount = 39.9 - 7.9;
    } else if (currentPlan === target) {
      // même plan
      amount = 0;
    } else if (currentPlan === "reindeer" && target === "family") {
      // downgrade interdit, on laisse 0 pour pouvoir détecter et afficher un message
      amount = 0;
    }

    setComputedAmount(parseFloat(amount.toFixed(2)));
  }, [currentPlan, plan]);

  const paypalOptions = useMemo(
    () =>
      clientId
        ? {
            "client-id": clientId,
            currency: "EUR",
            intent: "capture",
          }
        : null,
    [clientId]
  );

  if (!plan) {
    return (
      <div className="plan-payment-page">
        <div className="plan-payment-card unknown">
          <Link to="/pere-noel/premium" className="back-link">
            <ArrowLeft size={16} /> Retour aux formules Noël
          </Link>
          <h1>Plan introuvable 🎄</h1>
          <p>Le plan demandé n’existe pas ou n’est plus disponible.</p>
        </div>
      </div>
    );
  }

  const Icon = plan.icon;

  // Booleans pour l'UI et la logique d'upgrade
  const isSamePlan = currentPlan === plan.id;
  const isDowngrade =
    currentPlan === "reindeer" && plan.id === "family"; // on empêche

  const isUpgradeFamilyToReindeer =
    currentPlan === "family" && plan.id === "reindeer";

  const isFreeOrNone =
    currentPlan === "none" || currentPlan === "free" || !currentPlan;

  const canPay =
    isFormValid &&
    !isLoading &&
    !isSamePlan &&
    !isDowngrade &&
    computedAmount !== null &&
    computedAmount > 0;

  // ========================================================================
  // 4. Création de la commande PayPal côté serveur (Père Noël + upgrade)
  // ========================================================================
  const handleCreateOrder = async () => {
    try {
      setIsLoading(true);
      setErrorMsg("");

      const res = await apiClient.post("/paypal/create-santa-plan", {
        planId: plan.id,           // "family" | "reindeer"
        currentPlan: currentPlan,  // "none" | "free" | "family" | "reindeer"
        first_name: childName,
        last_name: "",
        email: parentEmail,
        childName,
        parentEmail,
        extraInfo,
      });

      if (!res.data || !res.data.id) {
        throw new Error("Réponse PayPal invalide (pas d'ID de commande).");
      }

      // On se met à jour avec le montant calculé par le backend (sécurité)
      if (typeof res.data.amount === "number") {
        setComputedAmount(parseFloat(res.data.amount.toFixed(2)));
      }

      console.log("[SantaPayment] create-santa-plan OK =>", res.data);
      return res.data.id; // PayPal Buttons a besoin de l’ID
    } catch (err) {
      console.error("Erreur createSantaPlanOrder PayPal:", err);

      const msg =
        err?.response?.data?.message ||
        "Impossible de créer la commande PayPal. Merci de réessayer dans quelques instants.";

      setErrorMsg(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================================================
  // 5. Capture de la commande une fois approuvée par l'utilisateur
  // ========================================================================
  const handleApprove = async (data, actions) => {
    try {
      setIsLoading(true);
      setErrorMsg("");

      console.log("[SantaPayment] onApprove data :", data);

      const res = await apiClient.post("/paypal/capture-order", {
        orderId: data.orderID, // ID PayPal
        formEmail: parentEmail,
        formFirstName: childName,
        formLastName: "", // optionnel
      });

      console.log("✅ Paiement capturé :", res.data);

      setSuccessMsg(
        "Merci ! Votre paiement a été validé. Le Père Noël prépare déjà sa surprise 🎅✨"
      );
      setErrorMsg("");

      const finalAmount =
        typeof res.data?.amount === "string"
          ? res.data.amount
          : computedAmount?.toFixed(2) || plan.price.toFixed(2);

      // On stocke les infos pour le récap du modal
      setLastOrder({
        orderId: data.orderID,
        transactionId: res.data?.transactionId,
        amount: finalAmount,
        planLabel: plan.label,
        childName,
        parentEmail,
      });

      setIsModalOpen(true);
    } catch (error) {
      console.error("Erreur captureOrder PayPal:", error);

      const serverMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de la validation du paiement. Vérifiez que le débit a été effectué avant de réessayer.";

      setErrorMsg(serverMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (err) => {
    console.error("Erreur PayPal globale:", err);
    setErrorMsg(
      "Une erreur PayPal est survenue. Vérifiez votre connexion ou réessayez."
    );
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Redirection vers la page du Père Noël avec le pack débloqué
    navigate(`/pere-noel?pack=${plan.id}`);
  };

  // Petit message explicatif selon le plan actuel
  let planInfoMessage = "";
  if (isFreeOrNone) {
    planInfoMessage =
      "Aucun pack Père Noël actif n’est détecté pour cet e-mail. Le prix affiché correspond au pack complet.";
  } else if (isSamePlan) {
    planInfoMessage = `Vous disposez déjà du plan ${PLAN_LABELS[currentPlan]}. Il n'est pas nécessaire de repayer.`;
  } else if (isUpgradeFamilyToReindeer) {
    planInfoMessage = `Vous avez déjà le plan Famille Magique. Le montant à payer correspond uniquement à la différence pour passer à Renne du Père Noël.`;
  } else if (isDowngrade) {
    planInfoMessage =
      "Vous avez déjà le plan le plus élevé (Renne du Père Noël). Il n’est pas possible de basculer vers un plan inférieur.";
  } else if (currentPlan === "reindeer") {
    planInfoMessage =
      "Vous avez déjà le plan Renne du Père Noël, vous êtes au niveau maximum 🎄✨";
  }

  return (
    <div className="plan-payment-page">
      <div className="plan-payment-card">
        <Link to="/pere-noel/premium" className="back-link">
          <ArrowLeft size={16} /> Retour aux formules Noël
        </Link>

        {/* HEADER PLAN */}
        <div className="plan-payment-header">
          <div className="plan-payment-icon">
            <Icon size={30} />
          </div>
          <div>
            <h1>{plan.label}</h1>
            <p className="plan-payment-subtitle">{plan.description}</p>
          </div>
        </div>

        {/* PRIX / MONTANT RÉEL */}
        <div className="plan-payment-price">
          <span className="plan-payment-price-main">
            {computedAmount !== null
              ? `${computedAmount.toFixed(2)} €`
              : plan.priceLabel}
          </span>
          <span className="plan-payment-price-tag">
            {isUpgradeFamilyToReindeer
              ? "Montant de l’upgrade (différence de prix)"
              : "Paiement unique, pas d’abonnement"}
          </span>
        </div>

        {/* INFO SUR LE PLAN ACTUEL */}
        <div className="plan-current-info">
          <p>
            <strong>Plan actuel détecté pour cet e-mail :</strong>{" "}
            {PLAN_LABELS[currentPlan] || "Inconnu"}
          </p>
          {planInfoMessage && (
            <p className="plan-current-info-detail">{planInfoMessage}</p>
          )}
        </div>

        {/* AVANTAGES RAPPELÉS */}
        <ul className="plan-payment-features">
          {plan.id === "family" && (
            <>
              <li>
                <CheckCircle2 size={18} /> Messages illimités avec le Père Noël
                jusqu’au 25 décembre
              </li>
              <li>
                <CheckCircle2 size={18} /> Lettre personnalisée envoyée au Père
                Noël + réponse PDF par e-mail
              </li>
              <li>
                <CheckCircle2 size={18} /> Photo-montage de votre enfant avec le
                Père Noël
              </li>
              <li>
                <CheckCircle2 size={18} /> Chanson personnalisée avec le prénom
                de l’enfant
              </li>
            </>
          )}

          {plan.id === "reindeer" && (
            <>
              <li>
                <CheckCircle2 size={18} />{" "}
                <strong>Tous les avantages du plan Famille Magique</strong>
              </li>
              <li>
                <CheckCircle2 size={18} /> Vidéo personnalisée d’environ 3
                minutes avec le Père Noël qui parle à l’enfant
              </li>
              <li>
                <CheckCircle2 size={18} /> Contenus livrés par e-mail (lien
                sécurisé)
              </li>
              <li>
                <CheckCircle2 size={18} /> Idéal pour un cadeau inoubliable
              </li>
            </>
          )}
        </ul>

        {/* FORMULAIRE INFOS ENFANT / PARENT */}
        <div className="plan-payment-form">
          <h2>Informations pour personnaliser la magie ✨</h2>

          <div className="form-row">
            <label>
              Prénom de l’enfant
              <input
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder="Ex : Lina, Yanis, Adam..."
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              E-mail du parent
              <input
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="exemple@domaine.fr"
              />
            </label>
            <p className="form-footnote">
              C’est sur cette adresse que vous recevrez la confirmation, la
              lettre, la chanson et/ou la vidéo. C’est aussi elle qui sert à
              détecter votre plan actuel.
            </p>
          </div>

          <div className="form-row">
            <label>
              Informations complémentaires (optionnel)
              <textarea
                rows={3}
                value={extraInfo}
                onChange={(e) => setExtraInfo(e.target.value)}
                placeholder="Âge de l’enfant, passions, message spécial, frères et sœurs, etc."
              />
            </label>
          </div>
        </div>

        {/* MESSAGES ERREUR / SUCCÈS */}
        {clientError && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            {clientError}
          </div>
        )}

        {errorMsg && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div className="alert alert-success" style={{ marginTop: 12 }}>
            {successMsg}
          </div>
        )}

        {/* PAYPAL */}
        <div style={{ marginTop: "24px" }}>
          {!clientId && !clientError && (
            <p style={{ fontSize: "0.9rem", opacity: 0.8 }}>
              Chargement de la plateforme de paiement sécurisée…
            </p>
          )}

          {paypalOptions && (
            <PayPalScriptProvider options={paypalOptions}>
              {canPay ? (
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill" }}
                  disabled={isLoading}
                  createOrder={handleCreateOrder}
                  onApprove={handleApprove}
                  onError={handleError}
                />
              ) : (
                <button
                  type="button"
                  className="btn-primary full-width big"
                  disabled
                >
                  <ArrowRight size={18} style={{ marginRight: 6 }} />
                  {isSamePlan
                    ? "Vous avez déjà ce plan"
                    : isDowngrade
                    ? "Vous avez déjà le plan le plus élevé"
                    : "Complétez les infos pour activer le paiement"}
                </button>
              )}
            </PayPalScriptProvider>
          )}
        </div>
      </div>

      {/* MODAL DE CONFIRMATION */}
      {isModalOpen && lastOrder && (
        <div className="modal-backdrop">
          <div className="modal">
            <h2>Commande confirmée 🎉</h2>
            <p>
              Merci pour votre confiance ! Votre pack{" "}
              <strong>{lastOrder.planLabel}</strong> est maintenant débloqué.
            </p>

            <div className="modal-recap">
              <p>
                <strong>Prénom de l’enfant :</strong> {lastOrder.childName}
              </p>
              <p>
                <strong>E-mail du parent :</strong> {lastOrder.parentEmail}
              </p>
              <p>
                <strong>Montant :</strong> {lastOrder.amount} €</p>
              {lastOrder.transactionId && (
                <p>
                  <strong>Référence de paiement :</strong>{" "}
                  {lastOrder.transactionId}
                </p>
              )}
              <p>
                <strong>ID de commande PayPal :</strong> {lastOrder.orderId}
              </p>
            </div>

            <button
              type="button"
              className="btn-primary full-width"
              onClick={handleModalClose}
            >
              OK, allons voir le Père Noël 🎅
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
